import type {
    CollectionQueryState,
    CollectionQueryItem,
    CollectionQueryStateFactory,
    CollectionQueryStateAdapter,
    CollectionQuerySelectors,
    CollectionQuerySelectorsFactory,
    CollectionQueryStateStatuses,
    CollectionQueryAdapter,
} from './collection-query.type';
import { createSelector } from '@ngrx/store';

export enum CollectionStateStatus {
    idle = 'idle', // initial state
    loading = 'loading', // loading data
    success = 'success', // data loaded
    failure = 'failure', // error while loading data
}

// HELPERS
function isQueryItemEmpty<T>(queryItem: T): boolean {
    if (queryItem === null) {
        return true;
    }

    if (Array.isArray(queryItem)) {
        return queryItem.length === 0;
    }

    return Boolean(queryItem);
}

// INITIAL STATE
function getInitialCollectionQueryState<T>(): CollectionQueryState<T> {
    return {
        queries: {},
    };
}

function getEmptyCollectionQueryItem<T>(): CollectionQueryItem<T> {
    return {
        item: null,
        status: CollectionStateStatus.idle,
        error: null,
    };
}

function createInitialCollectionStateFactory<T>(): CollectionQueryStateFactory<T> {
    function getInitialState(): any {
        return getInitialCollectionQueryState<T>();
    }

    return { getInitialState };
}
// END INITIAL STATE

// STATE ADAPTER
function createCollectionQueryStateAdapter<T>(): CollectionQueryStateAdapter<T> {
    function loadCollectionQueryRequest(state: CollectionQueryState<T>, id: string | number): CollectionQueryState<T> {
        const formattedId = id.toString();
        const item = getEmptyCollectionQueryItem<T>();
        item.status = CollectionStateStatus.loading;

        return { ...state, queries: { ...state.queries, [formattedId]: item } };
    }

    function loadCollectionQuerySuccess(
        item: T,
        id: string | number,
        state: CollectionQueryState<T>
    ): CollectionQueryState<T> {
        const formattedId = id.toString();
        const nextState = { ...state, queries: { ...state.queries } };
        nextState.queries[formattedId] = {
            ...nextState.queries[formattedId],
            item,
            status: CollectionStateStatus.success,
        };

        return nextState;
    }

    function loadCollectionQueryFailure(
        error: string,
        id: string | number,
        state: CollectionQueryState<T>
    ): CollectionQueryState<T> {
        const formattedId = id.toString();
        const nextState = { ...state, queries: { ...state.queries } };
        nextState.queries[formattedId] = {
            ...nextState.queries[formattedId],
            status: CollectionStateStatus.failure,
            error,
        };

        return nextState;
    }

    return {
        loadCollectionQueryRequest,
        loadCollectionQuerySuccess,
        loadCollectionQueryFailure,
    } as CollectionQueryStateAdapter<T>;
}
// END STATE ADAPTER

// SELECTORS
function createCollectionQuerySelectorsFactory<T>(): CollectionQuerySelectorsFactory<T> {
    function getSelectors(): CollectionQuerySelectors<T> {
        const selectCollectionQuery =
            (id: string) =>
            (state: CollectionQueryState<T>): CollectionQueryItem<T> => {
                if (!id || !state.queries[id]) {
                    return null;
                }

                return state.queries[id];
            };

        const selectCollectionQueryItem = (id: string) =>
            createSelector(selectCollectionQuery(id), (query: CollectionQueryItem<T>) => query?.item ?? null);
        const selectCollectionQueryStatus = (id: string) =>
            createSelector(selectCollectionQuery(id), (query: CollectionQueryItem<T>) => query?.status ?? null);

        const selectCollectionQueryStatuses = (id: string) =>
            createSelector(
                selectCollectionQueryItem(id),
                selectCollectionQueryStatus(id),
                (items: T, status: CollectionStateStatus): CollectionQueryStateStatuses => ({
                    isReady: status === CollectionStateStatus.success || status === CollectionStateStatus.failure,
                    isNotReady: status === CollectionStateStatus.idle || status === CollectionStateStatus.loading,
                    isEmpty: status === CollectionStateStatus.success && isQueryItemEmpty(items),
                    isNotEmpty: status === CollectionStateStatus.success && !isQueryItemEmpty(items),
                    isInitial: status === CollectionStateStatus.idle,
                    isLoading: status === CollectionStateStatus.loading,
                    isFailure: status === CollectionStateStatus.failure,
                })
            );

        return {
            selectCollectionQuery,
            selectCollectionQueryItem,
            selectCollectionQueryStatus,
            selectCollectionQueryStatuses,
        };
    }

    return { getSelectors };
}
// END SELECTORS

export function createCollectionQueryAdapter<T>(): CollectionQueryAdapter<T> {
    const stateFactory: CollectionQueryStateFactory<T> = createInitialCollectionStateFactory<T>();
    const selectorsFactory: CollectionQuerySelectorsFactory<T> = createCollectionQuerySelectorsFactory<T>();
    const stateAdapter: CollectionQueryStateAdapter<T> = createCollectionQueryStateAdapter<T>();

    return {
        ...stateFactory,
        ...stateAdapter,
        ...selectorsFactory,
    };
}
